import React from "react";
import { useTheme } from "react-jss";
import { useSelector } from "react-redux";
import classnames from "classnames";
import { FontAwesomeIcon } from "../shared/FontAwesome";
import type { Theme } from "../../types/Theme";
import { StoreState } from "../../models/states/StoreState";
import { shouldUseModernTheme } from "../../theme/navigationButtonThemeColors";
import { ModernSelectChevronDownIcon, ModernSelectChevronUpIcon } from "../shared/ModernSelectIcons";
import { isEqual } from "../../utils/lodash";
import useStyles from "./Select.styles";
import { RecalledText } from "../../containers/shared/RecalledText";
import { isNullOrEmpty, removeHtmlTags } from "../../utils";
import InputWrapper from "../shared/InputWrapper";
import { useKeyboardNavigation } from "../../hooks/useKeyboardNavigation";
import useQuestion from "../../hooks/useQuestion";
import { TextAnswerFieldNavigation } from "../controls/KeyboardNavigation/TextAnswerFieldNavigation";
import { isEmptyPlaceholderOption, hasRealSelectValue } from "./selectOptionUtils";

export interface Option {
    id: string;
    label: string;
    value: string;
    recalledLabel: string;
}

interface Props {
    id: string;
    labelId: string;
    openEmpty?: boolean;
    required?: boolean;
    value: string;
    options: Option[];
    minLength?: number;
    maxResults?: number;
    maxResultsMessage?: string;
    searchMethod?: "Exact" | "StartsWith" | "Contains";
    onChange(value: string): void;
    onBlur();
    onOptionSelected?(option: Option): void;
    size?: string;
    ariaAutocomplete?: "none" | "list" | "both";
    hasError?: boolean;
    /** Variable name used to read validation errors from the store (re-renders on validate). */
    validationVariableName?: string;
}

export default function AutoComplete(props: Props) {
    const {
        options,
        minLength,
        maxResults,
        maxResultsMessage,
        searchMethod,
        required,
        id,
        labelId,
        value,
        onBlur,
        onChange,
        size,
        onOptionSelected,
        ariaAutocomplete = "list",
        hasError = false,
        validationVariableName,
    } = props;

    const ref = React.useRef<HTMLDivElement | null>(null);
    /** After Escape or confirming a selection, suppress auto-reopen until user types or opens again (see open-on-type effect). */
    const suppressAutoOpenRef = React.useRef(false);
    const [dropdownWidth, setDropdownWidth] = React.useState(0);
    const [focusedOption, setFocusedOption] = React.useState<Option>();
    const [hovered, setHovered] = React.useState(false);
    const [focused, setFocused] = React.useState(false);
    const [expanded, setExpanded] = React.useState(false);
    const [keyboardListHighlight, setKeyboardListHighlight] = React.useState(false);
    const theme = useTheme() as Theme;
    const selectedOption =
        value && value.trim() !== ""
            ? options.find((o) => value.trim().toLowerCase() === o.label.trim().toLowerCase())
            : undefined;
    const hasSelectedValue =
        hasRealSelectValue([value]) &&
        selectedOption != null &&
        !isEmptyPlaceholderOption(selectedOption);
    const classes = useStyles({
        theme,
        size,
        autoComplete: true,
        selected: hasSelectedValue,
    } as any) as any;
    const useModernTheme = shouldUseModernTheme(theme);
    const validation = useSelector((state: StoreState) => state.interview.validation);
    const validationHasError = Boolean(
        validationVariableName &&
            (validation?.errorsByVariableName?.[validationVariableName]?.length ?? 0) > 0,
    );
    const inError = hasError || validationHasError;

    React.useLayoutEffect(() => {
        // Listen to scroll events to measure dropdown position.
        if (!expanded) {
            return undefined;
        }
        function measure() {
            if (!ref.current) return;
            const rect = ref.current.getBoundingClientRect();
            if (!isEqual(dropdownWidth, rect.width)) {
                setDropdownWidth(rect.width);
            }
        }
        window.addEventListener("resize", measure);
        window.addEventListener("scroll", measure, true);
        return function cleanup() {
            window.removeEventListener("resize", measure);
            window.removeEventListener("scroll", measure, true);
        };
    }, [expanded, dropdownWidth]);

    function focusOption(option?: Option) {
        setFocusedOption(option);
    }

    function selectOption(option) {
        onChange(option.label);
        if (onOptionSelected) {
            onOptionSelected(option);
        }
    }

    React.useEffect(() => {
        function clickOutside(event) {
            if (ref.current && ref.current.contains(event.target as Node)) {
                return;
            }
            closeListBox();
        }
        document.addEventListener("mousedown", clickOutside);
        return function cleanup() {
            document.removeEventListener("mousedown", clickOutside);
        };
    }, []);

    function resetListHighlight() {
        setKeyboardListHighlight(false);
        setFocusedOption(undefined);
    }

    const filteredOptions = React.useMemo(() => {
        if (minLength > 0 && (isNullOrEmpty(value) || value.length < minLength)) {
            return [];
        }

        let result = options;
        const trimmedLowerValue = value.trim().toLowerCase();

        if (isNullOrEmpty(trimmedLowerValue)) {
            return result;
        }

        switch (searchMethod) {
            case "Exact":
                result = result.filter(
                    (c) => c.recalledLabel.trim().toLowerCase() === trimmedLowerValue,
                );
                break;
            case "StartsWith":
                result = result.filter((c) =>
                    c.recalledLabel.trim().toLowerCase().startsWith(trimmedLowerValue),
                );
                break;
            default:
                result = result.filter((c) =>
                    c.recalledLabel.trim().toLowerCase().includes(trimmedLowerValue),
                );
                break;
        }
        return result;
    }, [value, minLength, options, searchMethod]);

    const displayMaxResultsMessage = maxResults && filteredOptions.length > maxResults;

    const limitedFilteredOptions = filteredOptions.slice(0, maxResults || 50);

    React.useEffect(() => {
        if (focusedOption && !limitedFilteredOptions.includes(focusedOption)) {
            setFocusedOption(undefined);
        }
    }, [focusedOption, limitedFilteredOptions]);

    function selectFirstOption() {
        const firstOption = limitedFilteredOptions[0];
        if (!firstOption) {
            return;
        }
        focusOption(firstOption);
    }

    function selectLastOption() {
        const lastOption = limitedFilteredOptions[limitedFilteredOptions.length - 1];
        if (!lastOption) {
            return;
        }
        focusOption(lastOption);
    }

    function selectPreviousOption() {
        if (limitedFilteredOptions.length === 0) {
            return;
        }
        const focusedIndex = limitedFilteredOptions.indexOf(focusedOption);
        const previousIndex = Math.max(0, focusedIndex - 1);
        const nextOption = limitedFilteredOptions[previousIndex];
        focusOption(nextOption);
    }

    function selectNextOption() {
        if (limitedFilteredOptions.length === 0) {
            return;
        }
        const focusedIndex = limitedFilteredOptions.indexOf(focusedOption);
        const nextIndex = Math.min(limitedFilteredOptions.length - 1, focusedIndex + 1);
        const nextOption = limitedFilteredOptions[nextIndex];
        focusOption(nextOption);
    }

    function focusInput() {
        elementReference.current?.focus();
    }

    function closeListBox(restoreFocus = false) {
        setExpanded(false);
        resetListHighlight();
        if (restoreFocus) {
            focusInput();
        }
    }

    function openListBox() {
        suppressAutoOpenRef.current = false;
        setExpanded(true);
    }
    const question = useQuestion();
    const [variable] = question?.variables ?? [];
    const { elementReference, onKeyDown } = useKeyboardNavigation(
        id,
        variable,
        question,
        TextAnswerFieldNavigation,
        null,
    );

    React.useEffect(() => {
        if (suppressAutoOpenRef.current) {
            return;
        }
        if (!expanded && focused && value.length >= (minLength || 0)) {
            openListBox();
        }
    }, [value, minLength, expanded, focused]);

    function onButtonKeyDown(e: React.KeyboardEvent) {
        // Only open listbox on space if dropdown is closed and input is empty, otherwise allow space input
        if (e.key === " " && !expanded && (!value || value.length === 0)) {
            openListBox();
            e.preventDefault();
        }
        if (e.key === "Escape") {
            if (expanded) {
                suppressAutoOpenRef.current = true;
                closeListBox(true);
                e.preventDefault();
            }
            return;
        }
        if (e.key === "ArrowUp") {
            openListBox();
            setKeyboardListHighlight(true);
            selectPreviousOption();
            e.preventDefault();
            return;
        }
        if (e.key === "ArrowDown") {
            openListBox();
            setKeyboardListHighlight(true);
            selectNextOption();
            e.preventDefault();
            return;
        }
        if (e.key === "Home" && expanded) {
            setKeyboardListHighlight(true);
            selectFirstOption();
            e.preventDefault();
            return;
        }
        if (e.key === "End" && expanded) {
            setKeyboardListHighlight(true);
            selectLastOption();
            e.preventDefault();
            return;
        }
        if (e.key === "Enter" && expanded && focusedOption) {
            suppressAutoOpenRef.current = true;
            selectOption(focusedOption);
            closeListBox(true);
            e.preventDefault();
            return;
        }
        if (e.key === "Tab") {
            onKeyDown(e);
        }
    }

    return (
        <InputWrapper size={size}>
            <div
                ref={ref}
                className={classnames(
                    classes.select,
                    hovered && !focused && !expanded && classes.selectHover,
                    expanded && classes.selectExpanded,
                    focused && !expanded && classes.selectFocused,
                )}
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
                onBlur={(e) => {
                    const target = e.relatedTarget || document.activeElement;
                    // If the element that *gained* focus is not contained within, close the list box.
                    if (ref.current && !ref.current.contains(target as Node)) {
                        closeListBox();
                        onBlur();
                    }
                }}
            >
                <input
                    ref={elementReference}
                    id={id}
                    role="combobox"
                    aria-autocomplete={ariaAutocomplete}
                    aria-expanded={expanded}
                    aria-controls={`${id}_listbox`}
                    aria-labelledby={labelId}
                    aria-required={required}
                    aria-activedescendant={expanded ? focusedOption?.id : undefined}
                    type="text"
                    aria-invalid={inError || undefined}
                    className={classes.selectContent}
                    onKeyDown={onButtonKeyDown}
                    onBlur={() => setFocused(false)}
                    onFocus={() => {
                        // Programmatic focus after close/selection fires focus; do not reopen while suppress is set.
                        if (!suppressAutoOpenRef.current && !minLength) {
                            openListBox();
                        }
                        setFocused(true);
                    }}
                    value={value}
                    autoComplete="off"
                    onChange={(e) => {
                        suppressAutoOpenRef.current = false;
                        onChange(e.target.value);
                    }}
                />
                {useModernTheme ? (
                    expanded ? (
                        <ModernSelectChevronUpIcon
                            className={classnames(
                                classes.icon,
                                classes.iconCaret,
                                classes.iconSvg,
                                classes.iconSvgCaret,
                            )}
                        />
                    ) : (
                        <ModernSelectChevronDownIcon
                            className={classnames(
                                classes.icon,
                                classes.iconCaret,
                                classes.iconSvg,
                                classes.iconSvgCaret,
                            )}
                        />
                    )
                ) : (
                    <FontAwesomeIcon
                        icon={expanded ? "caret-up" : "caret-down"}
                        className={classnames(classes.icon, classes.iconCaret)}
                    />
                )}
                {/* eslint-disable-next-line jsx-a11y/aria-activedescendant-has-tabindex */}
                <ul
                    id={`${id}_listbox`}
                    role="listbox"
                    className={classnames(
                        classes.listbox,
                        classes.dropDown,
                        !expanded && classes.hidden,
                        expanded ? classes.listboxExpanded : "",
                    )}
                    style={{ minWidth: dropdownWidth }}
                    tabIndex={-1}
                >
                    {limitedFilteredOptions.map((option) => (
                        <li
                            id={option.id}
                            key={option.id}
                            role="option"
                            onClick={(e) => {
                                suppressAutoOpenRef.current = true;
                                selectOption(option);
                                closeListBox(true);
                                e.preventDefault();
                            }}
                            className={classnames(
                                useModernTheme &&
                                    isEmptyPlaceholderOption(option) &&
                                    focusedOption !== option
                                    ? classes.optionPlaceholderModern
                                    : useModernTheme
                                      ? classes.optionModern
                                      : classes.option,
                                useModernTheme &&
                                    selectedOption === option &&
                                    !isEmptyPlaceholderOption(option) &&
                                    classes.optionSelectedModern,
                                useModernTheme &&
                                    keyboardListHighlight &&
                                    expanded &&
                                    focusedOption === option &&
                                    classes.optionHighlightedModern,
                                !useModernTheme &&
                                    keyboardListHighlight &&
                                    expanded &&
                                    focusedOption === option &&
                                    classes.autoCompleteOptionHighlighted,
                            )}
                            aria-label={removeHtmlTags(option.label)}
                            tabIndex={-1}
                            aria-selected={selectedOption === option ? true : undefined}
                        >
                            <RecalledText text={option.label} ariaHidden={true} />
                        </li>
                    ))}
                    {displayMaxResultsMessage && (
                        <li className={classes.message} tabIndex={-1}>
                            <RecalledText
                                text={maxResultsMessage.replace(/{max}/, maxResults.toString())}
                            />
                        </li>
                    )}
                </ul>
            </div>
        </InputWrapper>
    );
}
